import { useFormik } from 'formik';
import type { TFunction } from 'i18next';

import { successToastMessage } from '../../../components/layout/toast-root/toast';
import { useFirebaseAuth } from '../../../hooks/useFirebaseAuth';
import { getSignUpAgreementsValidationMessage } from '../../../redux/api/agreements/agreementPolicy';
import { handleAuthError } from '../../../utils/auth/auth-errors';
import { getSignUpFormValidation } from './FormikData';

const BACKEND_USER_EXISTS_CODE = 'signUp/backendUserExists';

export function useSignUpPage(
  t: TFunction,
  callbacks: {
    afterEmailSignUp: (oobCode?: string) => void;
    afterSocialSignUp: () => void;
  },
) {
  const {
    signUpWithPassword,
    signUpWithGoogle,
    signUpWithApple,
    signUpWithFacebook,
  } = useFirebaseAuth();

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getSignUpFormValidation(t),
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      acceptedAgreements: false,
    },
    onSubmit: submit,
  });

  const handleSubmitAttempt = (e: React.FormEvent<HTMLFormElement>) => {
    formik.setTouched({
      email: true,
      password: true,
      confirmPassword: true,
      acceptedAgreements: true,
    });
    formik.handleSubmit(e);
  };

  const handleSocialSignUpAttempt = async (
    signUpFn: () => Promise<void>,
  ): Promise<void> => {
    formik.setTouched({ acceptedAgreements: true });
    if (!formik.values.acceptedAgreements) {
      void formik.setFieldError(
        'acceptedAgreements',
        getSignUpAgreementsValidationMessage(t),
      );
      return;
    }
    void formik.setFieldError('acceptedAgreements', undefined);
    return signUpFn();
  };

  async function submit(formValues: {
    email: string;
    password: string;
    confirmPassword: string;
    acceptedAgreements: boolean;
  }) {
    try {
      await signUpWithPassword({
        email: formValues.email,
        password: formValues.password,
      });
      callbacks.afterEmailSignUp(undefined);
    } catch (error: unknown) {
      const code = (error as { code?: string })?.code;
      if (code === BACKEND_USER_EXISTS_CODE) {
        successToastMessage(t('signUp.alreadyRegisteredSigningIn'));
        callbacks.afterSocialSignUp();
        return;
      }
      console.error('Register error message: ', error);
      handleAuthError(error, t);
    }
  }

  async function signUpWithGoogleHandler() {
    try {
      await signUpWithGoogle();
      callbacks.afterSocialSignUp();
    } catch (error: unknown) {
      console.error('Sign up with Google error: ', error);
      handleAuthError(error, t);
    }
  }

  async function signUpWithAppleHandler() {
    try {
      await signUpWithApple();
      callbacks.afterSocialSignUp();
    } catch (error: unknown) {
      console.error('Sign up with Apple error: ', error);
      handleAuthError(error, t);
    }
  }

  async function signUpWithFacebookHandler() {
    try {
      await signUpWithFacebook();
      callbacks.afterSocialSignUp();
    } catch (error: unknown) {
      console.error('Sign up with Facebook error: ', error);
      handleAuthError(error, t);
    }
  }

  return {
    formik,
    handleSubmitAttempt,
    handleSocialSignUpAttempt,
    signUpWithGoogle: signUpWithGoogleHandler,
    signUpWithApple: signUpWithAppleHandler,
    signUpWithFacebook: signUpWithFacebookHandler,
  };
}
